import axios from 'axios'
import { toast } from 'react-toastify'

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api'

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token from Redux store
let store
export const injectStore = (_store) => {
  store = _store
}

axiosInstance.interceptors.request.use(
  config => {
    const token = store?.getState()?.auth?.accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

// ── Refresh queue ─────────────────────────────────────────────────────────────
// When multiple requests get a 401 simultaneously, only one refresh call is made.
// All other pending requests are queued and replayed once the token is refreshed.
let isRefreshing = false
let refreshQueue = [] // [{ resolve, reject }]

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token)
  })
  refreshQueue = []
}

// ── Response interceptor ──────────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      // Queue this request until the ongoing refresh completes
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject })
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`
        return axiosInstance(originalRequest)
      }).catch(err => Promise.reject(err))
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const response = await axios.post(
        `${API_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      )

      const { accessToken, user } = response.data

      if (store && accessToken) {
        store.dispatch({ type: 'auth/setCredentials', payload: { accessToken, user } })
      }

      processQueue(null, accessToken)
      originalRequest.headers.Authorization = `Bearer ${accessToken}`
      return axiosInstance(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)

      if (store) {
        store.dispatch({ type: 'auth/clearCredentials' })
      }
      if (window.location.pathname !== '/login') {
        toast.info('Your session has expired. Please log in again.')
        setTimeout(() => { window.location.href = '/login' }, 1500)
      }
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default axiosInstance
